import React, { useEffect, useRef } from "react";
import { LayoutChangeEvent } from "react-native";
import { useTheme } from "styled-components/native";
import styled from "styled-components/native";
import { Spacer } from "@components/atoms";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { ProductCategory } from "@utils/constants";
import { isGemstone, isJewelry } from "@utils/product/productValidators";
import ProductHeaderDetail from "../utils/ProductHeaderDetail";
import PDPPriceContainer from "../utils/PriceContainer";
import { useTranslation } from "react-i18next";
import { css } from "styled-components/native";

interface PdpHeaderProps {
  // Data
  itemDetail: any;
  ref: any;
  productType: ProductCategory;
  priceMode: number;
  productColor: string;
  productAlphaColor: string;
  productName: string;
  showAvailabilityDot: boolean;
  isModal: boolean;
  isGuest: boolean;
  fromComponent?: boolean;
  routeReferrer: string;

  // Layout
  sizes: {
    spacing: number;
  };

  // Layout handlers
  onLayout?: (e: any) => void;
  onProductScrollOffset?: (y: number | null) => void;
}

const PdpHeader = ({
  ref,
  itemDetail,
  productType,
  priceMode,
  productColor,
  productAlphaColor,
  productName,
  showAvailabilityDot,
  isModal,
  isGuest,
  fromComponent,
  sizes,
  onLayout,
  routeReferrer,
  onProductScrollOffset,
}: PdpHeaderProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const parentYRef = useRef(0);
  const sectionYRef = useRef(0);
  const priceYRef = useRef(0);
  const laidOutRef = useRef({ parent: false, section: false, price: false });

  const publishProductOffset = () => {
    if (
      !onProductScrollOffset ||
      !laidOutRef.current.parent ||
      !laidOutRef.current.section ||
      !laidOutRef.current.price
    ) {
      return;
    }

    onProductScrollOffset(
      parentYRef.current + sectionYRef.current + priceYRef.current
    );
  };

  const priceSectionVisible =
    priceMode !== PriceModes.GREEN && !(isModal && fromComponent);

  useEffect(() => {
    if (!priceSectionVisible) {
      laidOutRef.current.section = false;
      laidOutRef.current.price = false;
      onProductScrollOffset?.(null);
    }
    return () => onProductScrollOffset?.(null);
  }, [onProductScrollOffset, priceSectionVisible]);

  const onParentLayout = (event: LayoutChangeEvent) => {
    onLayout?.(event);
    parentYRef.current = event.nativeEvent.layout.y;
    laidOutRef.current.parent = true;
    publishProductOffset();
  };

  const onSectionLayout = (event: LayoutChangeEvent) => {
    sectionYRef.current = event.nativeEvent.layout.y;
    laidOutRef.current.section = true;
    publishProductOffset();
  };

  const onPriceLayout = (event: LayoutChangeEvent) => {
    priceYRef.current = event.nativeEvent.layout.y;
    laidOutRef.current.price = true;
    publishProductOffset();
  };

  return (
    <ParentViewContainer onLayout={onParentLayout}>
      <ProductHeaderDetail
        itemDetail={itemDetail}
        productType={productType}
        isJewelry={isJewelry(productType)}
        isGemstone={isGemstone(productType)}
        productColor={productColor}
        priceMode={priceMode}
        productAlphaColor={productAlphaColor}
        productName={productName}
        showAvailabilityDot={showAvailabilityDot}
        isModal={isModal}
        routeReferrer={routeReferrer}
      />
      {priceMode !== PriceModes.GREEN && (
        <ViewContainer
          isGem={isGemstone(productType)}
          onLayout={onSectionLayout}
        >
          <Spacer size={sizes.spacing} />
          {!(isModal && fromComponent) && (
            <PDPPriceContainer
              ref={ref}
              isJewelry={isJewelry(productType)}
              isGuest={isGuest}
              theme={theme}
              t={t}
              productColor={productColor}
              itemDetail={itemDetail}
              priceMode={priceMode}
              productType={productType}
              onLayout={onPriceLayout}
            />
          )}
        </ViewContainer>
      )}
    </ParentViewContainer>
  );
};

const ParentViewContainer = styled.View`
  width: 100%;
  flex-shrink: ${({ theme }) => (theme.isLargeScreen ? 1 : 0)};
  ${({ theme }) =>
    theme.isMobile &&
    css`
      padding-left: 10px;
      padding-right: 10px;
    `}
`;

const ViewContainer = styled.View<{ isGem: boolean }>`
  padding-left: 0px;
  padding-right: 0px;
  padding-bottom: ${({ isGem }) => (isGem ? 8 : 0)}px;
`;

export default PdpHeader;
